/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Fragment, type ReactElement } from 'react';
import { ChevronRight } from 'lucide-react';
import { Link } from '@/components/link';

export type PageBreadcrumbItem = {
    label: string;
    /** Omit for the current page (rendered bold, not a link) */
    to?: string;
};

/**
 * Site-wide breadcrumb, styled after the PDP breadcrumb (see `.pdp-breadcrumbs` in base.css):
 * grey 14px links, 12px light-grey chevrons 8px either side, current page in bold #17171B.
 * Spacing around the row is left to the caller via `className`.
 */
export function PageBreadcrumbs({
    items,
    className,
    ariaLabel = 'Breadcrumb',
}: {
    items: PageBreadcrumbItem[];
    className?: string;
    ariaLabel?: string;
}): ReactElement {
    return (
        <nav aria-label={ariaLabel} className={className}>
            <ol className="flex flex-wrap items-center font-lato text-sm font-normal leading-4 text-[#727272]">
                {items.map((item, index) => {
                    const isCurrent = !item.to;
                    return (
                        <Fragment key={`${item.label}-${index}`}>
                            {index > 0 && (
                                <li aria-hidden="true" className="flex items-center">
                                    <ChevronRight className="mx-2 size-3 shrink-0 text-[#8C8C8C]" />
                                </li>
                            )}
                            <li className="flex items-center min-w-0">
                                {isCurrent ? (
                                    <span
                                        aria-current="page"
                                        className="font-lato text-sm font-bold leading-5 text-[#17171B] truncate">
                                        {item.label}
                                    </span>
                                ) : (
                                    <Link
                                        to={item.to as string}
                                        className="font-lato text-sm font-normal leading-4 text-[#727272] transition-colors hover:text-black">
                                        {item.label}
                                    </Link>
                                )}
                            </li>
                        </Fragment>
                    );
                })}
            </ol>
        </nav>
    );
}

export default PageBreadcrumbs;
